// Per-port VLAN helpers (tagged/untagged trunk model).
//
// A device keeps its own management/device VLAN (`data.vlanRef`) independently
// of its ports. Each port may carry ONE untagged/access VLAN (`untaggedVlanRef`)
// and any number of tagged VLANs (`taggedVlanRefs`, trunk).
//
// Port colouring rule (panel/detail rendering):
//   1. explicit untagged VLAN colour if set, otherwise
//   2. fall back to the PEER device's VLAN colour (legacy patch-panel logic).
//
// Pure JS — also imported by the Node round-trip test, so no DOM/React here.
import { getAllEdges, findDeviceGlobal } from './detail';

export function vlanById(doc, ref) {
  if (!ref) return null;
  return (doc?.vlans || []).find((v) => v.id === ref) || null;
}

export function portUntaggedVlan(port, doc) {
  return vlanById(doc, port?.untaggedVlanRef);
}

export function portTaggedVlans(port, doc) {
  const refs = Array.isArray(port?.taggedVlanRefs) ? port.taggedVlanRefs : [];
  return refs
    .map((r) => vlanById(doc, r))
    .filter(Boolean)
    .sort((a, b) => a.vlanId - b.vlanId);
}

// Map portId -> peer device VLAN colour (or null). Neutral if no connection /
// the peer has no VLAN, or if the port connects to multiple differing VLANs.
export function portPeerVlanColors(node, doc) {
  const map = {};
  if (!node || !doc) return map;
  getAllEdges(doc).forEach((e) => {
    let portId = null;
    let peerId = null;
    if (e.source === node.id && e.sourcePortId) {
      portId = e.sourcePortId;
      peerId = e.target;
    } else if (e.target === node.id && e.targetPortId) {
      portId = e.targetPortId;
      peerId = e.source;
    }
    if (!portId) return;
    const peer = findDeviceGlobal(doc, peerId);
    const v = peer && (doc.vlans || []).find((x) => x.id === peer.data?.vlanRef);
    const color = v ? v.color : null;
    if (!(portId in map)) map[portId] = color;
    else if (map[portId] !== color) map[portId] = null; // conflicting VLANs -> neutral
  });
  return map;
}

// Map portId -> resolved colour: explicit untagged VLAN colour first, else peer
// fallback. Returns { color, fromUntagged } per port.
export function portColorMap(node, doc) {
  const resolved = resolvePortVlans(node, doc);
  const out = {};
  Object.keys(resolved).forEach((pid) => {
    out[pid] = { color: resolved[pid].color, fromUntagged: resolved[pid].fromUntagged };
  });
  return out;
}

// Build a portId -> group map from a device's portGroups (config groups).
export function portGroupMap(node) {
  const map = {};
  (node?.data?.portGroups || []).forEach((g) => {
    (g.memberIds || []).forEach((pid) => {
      map[pid] = g;
    });
  });
  return map;
}

// Full per-port VLAN resolution honouring config groups. For grouped ports the
// effective config comes from the group; otherwise from the port itself.
// Colour = explicit untagged VLAN colour, else peer-device VLAN colour.
// Returns portId -> { untaggedVlan, taggedVlans:[vlan], color, fromUntagged,
//                     groupId, groupName }.
export function resolvePortVlans(node, doc) {
  const peer = portPeerVlanColors(node, doc);
  const groupOf = portGroupMap(node);
  const out = {};
  (node?.data?.ports || []).forEach((p) => {
    const g = groupOf[p.id] || null;
    const untaggedRef = g ? g.untaggedVlanRef : p.untaggedVlanRef;
    const taggedRefs = g ? g.taggedVlanRefs : p.taggedVlanRefs;
    const untaggedVlan = vlanById(doc, untaggedRef);
    const taggedVlans = (Array.isArray(taggedRefs) ? taggedRefs : [])
      .map((r) => vlanById(doc, r))
      .filter(Boolean)
      .sort((a, b) => a.vlanId - b.vlanId);
    const color = untaggedVlan ? untaggedVlan.color : peer[p.id] || null;
    out[p.id] = {
      untaggedVlan,
      taggedVlans,
      color,
      fromUntagged: !!untaggedVlan,
      groupId: g ? g.id : null,
      groupName: g ? g.name : null,
    };
  });
  return out;
}

// Human-readable VLAN summary for a resolved port entry (tooltips/titles).
export function resolvedPortTitle(r, portName, lang = 'de') {
  const parts = [portName || ''];
  if (r?.groupName) parts.push((lang === 'en' ? 'Group ' : 'Gruppe ') + r.groupName);
  if (r?.untaggedVlan) parts.push('Untagged VLAN ' + r.untaggedVlan.vlanId);
  if (r?.taggedVlans?.length) parts.push('Tagged ' + r.taggedVlans.map((v) => v.vlanId).join(', '));
  return parts.filter(Boolean).join(' \u00b7 ');
}

// Human-readable VLAN summary for a port (used in tooltips/titles).
export function portVlanTitle(port, doc, lang = 'de') {
  const u = portUntaggedVlan(port, doc);
  const tagged = portTaggedVlans(port, doc);
  const parts = [port?.name || ''];
  if (u) parts.push((lang === 'en' ? 'Untagged VLAN ' : 'Untagged VLAN ') + u.vlanId);
  if (tagged.length) parts.push((lang === 'en' ? 'Tagged ' : 'Tagged ') + tagged.map((v) => v.vlanId).join(', '));
  return parts.filter(Boolean).join(' \u00b7 ');
}
